<template>
    <!-- Tracking-link content (deep link: ?section=links-softswiss) -->
    <section :id="linksSectionId" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">Softswiss — Tracking links</h2>
        <Accordion :multiple="true" v-model:activeIndex="linksAccordion">
            <AccordionTab header="Finding the campaign key">
                <div class="text-sm leading-relaxed">
                    <p class="mb-3">
                        Softswiss identifies each promo (per brand × geo × commission) by a numeric <strong>ID</strong>
                        in its partner dashboard. That ID is what we save as the campaign key in AffiliationSpace /
                        Voonix. It's not in the tracking URL itself — you have to look it up on the partner side and
                        match it to the promo code that IS in the URL.
                    </p>

                    <ol class="list-decimal ml-6 mb-4 space-y-1">
                        <li>Open the operator's partner dashboard's <strong>Promos</strong> page — e.g.
                            <a href="https://dashboard.redmont-partners.com/partner/promos"
                               target="_blank" rel="noopener noreferrer" class="link-inline">
                                dashboard.redmont-partners.com/partner/promos
                            </a>.
                            (Every Softswiss operator has the same layout under
                            <code>/partner/promos</code>.)</li>
                        <li>Take the <strong>promo code</strong> from your tracking URL — it's the last path
                            segment before the query string. Example:<br />
                            <code class="bg-black/40 px-1 py-0.5 rounded">https://superspins777.net/<span class="text-green-400">dpzzmzdkl</span>?visit_id={clickid}</code>
                            → promo code <code>dpzzmzdkl</code>.</li>
                        <li>In the <strong>My promos</strong> table, find the row whose <strong>Promo code</strong>
                            column matches (in the screenshot below: <code>dpzzmzdkl</code>).</li>
                        <li>Copy the value in that row's <strong>ID</strong> column — that's the campaign key
                            (in the example: <code>515644</code>).</li>
                    </ol>

                    <p class="mb-2 font-medium">Softswiss partner dashboard — My promos</p>
                    <div class="mb-4">
                        <img :src="myPromosScreenshot"
                             alt="Softswiss partner dashboard — My promos page showing promo codes and IDs"
                             class="rounded border border-gray-700"
                             style="width: 100%; max-width: 1400px; height: auto;" />
                        <p class="text-xs text-gray-400 mt-1">
                            Match the <strong>Promo code</strong> to the last path segment of your tracking URL,
                            then read the <strong>ID</strong> from the same row.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs mb-4">
                        <p class="font-semibold mb-1">Match by promo code, not by Brand or Campaign</p>
                        <p>
                            The Brand / Campaign / Commission columns can look identical across rows (e.g. all say
                            "Super-spin"). Only the <strong>Promo code</strong> is guaranteed unique per tracking URL —
                            always match on that, then read the ID. Grabbing the wrong ID silently mis-attributes
                            postbacks to another promo.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs mb-4">
                        <p class="font-semibold mb-1">One partner dashboard per operator</p>
                        <p>
                            Every Softswiss-backed operator (Redmont, Yolo, etc.) has its own partner dashboard on a
                            unique subdomain, but the <code>/partner/promos</code> path and the table layout shown
                            here are identical. If the URL doesn't fit, ask the operator for their partner dashboard
                            login — the page you land on will look the same.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs">
                        <p class="font-semibold mb-1">Auto-appended on paste: <code>?visit_id={clickid}</code></p>
                        <p>
                            When you paste the raw URL into <strong>Affiliate Link</strong>, the enricher appends
                            <code>?visit_id={clickid}</code> for you if it's missing. You still have to type the
                            campaign key into <strong>Internal Note</strong> yourself — the URL doesn't carry the
                            numeric ID.
                        </p>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </section>

    <!-- New-login setup (deep link: ?section=token-softswiss) — its own section
         so it can be shared/linked independently of the tracking-link content. -->
    <section :id="tokenSectionId" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">Softswiss — Setting up a new login</h2>
        <Accordion :multiple="true" v-model:activeIndex="tokenAccordion">
            <AccordionTab header="Getting the Statistic token (key1)">
                <div class="text-sm leading-relaxed">
                    <p class="mb-3">
                        When creating a new Voonix advertiser under Softswiss in AffiliationSpace, the
                        <strong>Key 1</strong> field is required. That value is the operator's
                        <strong>Statistic token</strong> — it lives on the operator's partner panel and has to be
                        copied over by hand.
                    </p>

                    <ol class="list-decimal ml-6 mb-4 space-y-1">
                        <li>Log into the operator's partner panel using the credentials that will go into
                            <strong>Username</strong> / <strong>Password</strong> on the AffiliationSpace form.
                            Example URL:
                            <a href="https://admin.kawaii.partners/partner/profile"
                               target="_blank" rel="noopener noreferrer" class="link-inline">
                                admin.kawaii.partners/partner/profile
                            </a>
                            (every Softswiss-backed operator has its own subdomain — the path is the same).</li>
                        <li>Open the side menu → <strong>My account</strong> → <strong>Account info</strong>.</li>
                        <li>Copy the value next to <strong>Statistic token</strong> (it's a 32-character hex string
                            like <code>08ec862a91416fc35c5267d2b31fd311</code>).</li>
                        <li>Paste it into <strong>Key 1</strong> on the AS "Create new Voonix advertiser" form.</li>
                    </ol>

                    <p class="mb-2 font-medium">Where to find it in the partner panel</p>
                    <div class="mb-4">
                        <img :src="statisticTokenScreenshot"
                             alt="Softswiss partner panel — My account → Account info showing the Statistic token"
                             class="rounded border border-gray-700"
                             style="width: 100%; max-width: 1400px; height: auto;" />
                    </div>

                    <div class="p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs mb-4">
                        <p class="font-semibold mb-1">Each login has its own token</p>
                        <p>
                            The Statistic token is scoped to the partner account you're logged in as. If you're
                            setting up multiple logins for the same operator (e.g. different geos handled by
                            different accounts), each one has its own token — log into each account separately and
                            copy the token from that account's Account info page.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs">
                        <p class="font-semibold mb-1">If the token stops working, click <em>Expire</em> + re-copy</p>
                        <p>
                            The Account info page has an <strong>Expire</strong> link next to the token that rotates
                            it. If Voonix suddenly stops pulling data for a Softswiss login, someone may have expired
                            it on their side — grab the new token from the same place and update Key 1 on the Voonix
                            advertiser in AS.
                        </p>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </section>
</template>

<script setup>
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { useHelpSection } from './useHelpSection';
import myPromosScreenshot from '@/assets/docs/softswiss-my-promos.png';
import statisticTokenScreenshot from '@/assets/docs/softswiss-statistic-token.png';

// Two independently-linkable sections in one file:
//   ?section=links-softswiss  → the tracking-link / campaign-key lookup
//   ?section=token-softswiss  → the new-login Statistic token lookup
// Destructure at the top level so Vue's template auto-unwrap sees the refs.
// Nested access (linksSection.accordionActive) does NOT auto-unwrap and would
// make Accordion's v-model receive a Ref object instead of its value.
const { sectionId: linksSectionId, accordionActive: linksAccordion } = useHelpSection('links-softswiss');
const { sectionId: tokenSectionId, accordionActive: tokenAccordion } = useHelpSection('token-softswiss');
</script>

<style scoped>
.link-inline {
    color: var(--primary-color, #3b82f6);
    text-decoration: underline;
    cursor: pointer;
}
.link-inline:hover {
    color: #93c5fd;
}
</style>
